import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { plato_api_question_answer_key_html_question_path } from '@/modules/routes';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import { IQuestion } from '@/components/staffer/Grading/types';
import GradingView from '@/components/common/Forms/Questions/DragAndDrop/Answer/GradingView';
import SlideshowImage from 'modules/SlideshowImage';

interface AnswerKeyProps {
  question: IQuestion
}

const AnswerKey = ({ question } : AnswerKeyProps) => {
  const filters = useGradingStore(state => state.filters);
  const { answerKeyFilter } = filters;
  const [answerKeyHtml, setAnswerKeyHtml] = useState<string>('');
  const [showAnswerKey, setShowAnswerKey] = useState(answerKeyFilter === 'show');
  const toggleAnswerKey = () => setShowAnswerKey(!showAnswerKey);
  const answerKeyContainerRef = useRef<HTMLDivElement>(null);

  const dragAndDrop = question.type === 'DragAndDropQuestion';

  const answerKeyQuery = useQuery(['answerKey', question.id], () => (
    Axios.get(plato_api_question_answer_key_html_question_path(question.id))
      .then(res => res.data)
  ));

  useEffect(() => {
    setShowAnswerKey(answerKeyFilter === 'show');
  }, [answerKeyFilter]);

  useEffect(() => {
    if (answerKeyQuery.isLoading || dragAndDrop) return;
    setAnswerKeyHtml(answerKeyQuery.data);
  }, [answerKeyQuery.isLoading]);

  useEffect(() => {
    if (!answerKeyContainerRef.current || !answerKeyHtml || dragAndDrop) return;

    const imgElements = answerKeyContainerRef.current.querySelectorAll('img[data-zoomurl]');
    imgElements.forEach((img) => {
      const slideshowImage = new SlideshowImage();
      slideshowImage.init(img as HTMLImageElement);
    });
  }, [answerKeyHtml, dragAndDrop]);

  const answerKey = () => {
    if (dragAndDrop) {
      return (
        <div className={styles.dndAnswerContainer} id={`drag-and-drop-answer-key-${question.id}`}>
          <GradingView locale="en" parentContainerId={`drag-and-drop-answer-key-${question.id}`} question={question} />
        </div>
      );
    }

    return (
      <div
        ref={answerKeyContainerRef}
        className={styles.questionAnswerKeyContainer}
        dangerouslySetInnerHTML={{ __html: answerKeyHtml }}
      />
    );
  };

  return (
    <div className="tw-mt-2.5">
      <button
        className={styles.answerKeyToggle}
        type="button"
        onClick={toggleAnswerKey}
      >
        Answer Key <i aria-hidden className={`fa tw-ml-0.5 ${showAnswerKey ? 'fa-caret-down' : 'fa-caret-right'}`} />
      </button>
      {showAnswerKey && answerKey()}
    </div>
  );
};

export default AnswerKey;
